import { BooleanInput, coerceBooleanProperty } from "@angular/cdk/coercion";
import { Directive, ElementRef, HostListener, Input, OnDestroy, Renderer2 } from "@angular/core";

const enum Cursor {
    Grabbing = "grabbing",
    Grab = "grab",
}

@Directive({
    selector: "[dtmUiHorizontalScroll]",
})
export class HorizontalScrollDirective implements OnDestroy {
    @Input() public set dtmUiHorizontalScroll(value: BooleanInput) {
        this.isDisabled = !coerceBooleanProperty(value);

        if (this.isDisabled) {
            this.renderer.removeStyle(this.el.nativeElement, "cursor");
        } else {
            this.renderer.setStyle(this.el.nativeElement, "cursor", Cursor.Grab);
        }
    }

    private isDragging = false;
    private isDisabled = false;
    private startX = 0;
    private scrollLeft = 0;

    private moveListener?: () => void;
    private stopListener?: () => void;
    private mouseLeaveListener?: () => void;

    constructor(private readonly el: ElementRef, private readonly renderer: Renderer2) {
        this.renderer.setStyle(this.el.nativeElement, "cursor", Cursor.Grab);
    }

    @HostListener("mousedown", ["$event"])
    @HostListener("touchstart", ["$event"])
    private onGrab(event: MouseEvent | TouchEvent): void {
        if (this.isDisabled) {
            return;
        }

        this.isDragging = true;
        const isMouseEvent = event instanceof MouseEvent;
        this.startX = isMouseEvent ? event.clientX : event.touches[0].clientX;
        this.scrollLeft = this.el.nativeElement.scrollLeft;

        this.renderer.setStyle(this.el.nativeElement, "cursor", Cursor.Grabbing);

        this.moveListener = this.renderer.listen(this.el.nativeElement, isMouseEvent ? "mousemove" : "touchmove", this.onMove);
        this.stopListener = this.renderer.listen(this.el.nativeElement, isMouseEvent ? "mouseup" : "touchend", this.onRelease);

        if (isMouseEvent) {
            this.mouseLeaveListener = this.renderer.listen(this.el.nativeElement, "mouseleave", this.onRelease);
        }
    }

    private onMove = (event: MouseEvent | TouchEvent): void => {
        if (!this.isDragging) {
            return;
        }

        const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
        const deltaX = clientX - this.startX;
        this.el.nativeElement.scrollLeft = this.scrollLeft - deltaX;
    };

    private onRelease = (): void => {
        this.isDragging = false;
        this.renderer.setStyle(this.el.nativeElement, "cursor", Cursor.Grab);

        this.moveListener?.();
        this.stopListener?.();
        this.mouseLeaveListener?.();
    };

    public ngOnDestroy(): void {
        this.onRelease();
    }
}
